// src/services/dsaDocuments.js
import { FUNCTION_URL, FUNCTION_KEY } from '../utils/constants';

function addFunctionKeyToUrl(url) {
  if (FUNCTION_KEY) {
    url.searchParams.set('code', FUNCTION_KEY);
  }

  return url;
}

function getFilenameFromContentDisposition(response, fallbackFilename) {
  const contentDisposition =
    response.headers.get('content-disposition') || '';

  const utf8Match = contentDisposition.match(
    /filename\*=UTF-8''([^;]+)/i
  );

  if (utf8Match?.[1]) {
    try {
      return decodeURIComponent(utf8Match[1]);
    } catch {
      return utf8Match[1];
    }
  }

  const filenameMatch = contentDisposition.match(
    /filename="?([^";]+)"?/i
  );

  return filenameMatch?.[1] || fallbackFilename;
}

function downloadBlob(blob, filename) {
  const downloadUrl = window.URL.createObjectURL(blob);
  const link = document.createElement('a');

  link.href = downloadUrl;
  link.download = filename;

  document.body.appendChild(link);
  link.click();
  link.remove();

  window.URL.revokeObjectURL(downloadUrl);
}

async function readErrorResponse(response, prefix) {
  const bodyText = await response.text();
  let detail = bodyText.slice(0, 200);

  try {
    const json = JSON.parse(bodyText);

    detail =
      json.error ||
      json.message ||
      json.detail ||
      detail;
  } catch {
    // The response is not JSON.
  }

  throw new Error(
    `${prefix}: ${response.status} - ${detail}`
  );
}

/**
 * Fetches the personalized DSA before acceptance.
 *
 * @param {Object} params
 * @param {string} params.userEmail
 * @param {'html'|'pdf'} params.format
 * @param {boolean} params.download
 * @returns {Promise<Object>}
 */
export const getDsaDocument = async ({
  userEmail,
  format = 'html',
  download = false,
}) => {
  if (!userEmail) {
    throw new Error('User email parameter is required');
  }

  const url = new URL(
    `${FUNCTION_URL}/api/v1/dsa/document`
  );

  url.searchParams.set('email', userEmail);
  url.searchParams.set('format', format);
  addFunctionKeyToUrl(url);

  const response = await fetch(url.toString(), {
    method: 'GET',
  });

  if (!response.ok) {
    await readErrorResponse(
      response,
      'Error fetching DSA document'
    );
  }

  const contentType =
    response.headers.get('content-type') || '';

  if (
    format === 'pdf' &&
    contentType.includes('application/pdf')
  ) {
    const blob = await response.blob();

    const filename = getFilenameFromContentDisposition(
      response,
      `DSA_${userEmail.replace(
        /[^a-zA-Z0-9@._+-]/g,
        '_'
      )}.pdf`
    );

    if (download) {
      downloadBlob(blob, filename);

      return {
        success: true,
        filename,
      };
    }

    return {
      contentType,
      body: blob,
      filename,
      isPdf: true,
    };
  }

  const bodyText = await response.text();

  return {
    contentType,
    body: bodyText,
    filename: getFilenameFromContentDisposition(
      response,
      null
    ),
    isHtml: contentType.includes('text/html'),
    isPdf: false,
  };
};

/**
 * Fetches the archived DSA after acceptance.
 *
 * @param {Object} params
 * @param {Function} params.getAccessTokenSilently
 * @param {'md'|'pdf'} params.format
 * @param {boolean} params.download
 * @returns {Promise<Object>}
 */
export const getAcceptedDsaDocument = async ({
  getAccessTokenSilently,
  format = 'pdf',
  download = false,
}) => {
  if (typeof getAccessTokenSilently !== 'function') {
    throw new Error(
      'getAccessTokenSilently is not a function. ' +
      'Please check Auth0 integration.'
    );
  }

  const accessToken = await getAccessTokenSilently({
    authorizationParams: {
      audience: process.env.REACT_APP_AUTH0_AUDIENCE,
    },
  });

  const url = new URL(
    `${FUNCTION_URL}/api/v1/dsa/accepted-document`
  );

  url.searchParams.set('format', format);
  addFunctionKeyToUrl(url);

  const response = await fetch(url.toString(), {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
  });

  if (!response.ok) {
    await readErrorResponse(
      response,
      'Error fetching accepted DSA'
    );
  }

  const contentType =
    response.headers.get('content-type') || '';

  if (
    format === 'pdf' &&
    contentType.includes('application/pdf')
  ) {
    const blob = await response.blob();

    const filename = getFilenameFromContentDisposition(
      response,
      'accepted_dsa.pdf'
    );

    if (download) {
      downloadBlob(blob, filename);

      return {
        success: true,
        filename,
      };
    }

    return {
      contentType,
      body: blob,
      filename,
      isPdf: true,
    };
  }

  const bodyText = await response.text();

  return {
    contentType,
    body: bodyText,
    filename: getFilenameFromContentDisposition(
      response,
      null
    ),
    isPdf: false,
  };
};